"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { cn } from "../../lib/utils";
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart";

// CONTAINER STYLING
const chartContainerVariants = cva(
  "group relative flex w-full flex-col gap-2 transition-all duration-200",
  {
    variants: {
      variant: {
        card: [
          "rounded-2xl bg-card p-4 text-card-foreground ring-1 ring-foreground/10",
          "hover:ring-foreground/20",
        ],
        outline: ["rounded-lg border-2 p-4", "hover:border-primary/50"],
        ghost: ["rounded-lg p-4 hover:bg-muted/50"],
        minimal: "p-2",
      },
      size: {
        sm: "gap-1",
        md: "gap-2",
        lg: "gap-3",
      },
    },
    defaultVariants: {
      variant: "card",
      size: "md",
    },
  }
);

// TITLE STYLING
const chartTitleVariants = cva("font-semibold tracking-tight", {
  variants: {
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

// DESCRIPTION STYLING
const chartDescriptionVariants = cva("text-muted-foreground leading-relaxed", {
  variants: {
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface LineChartItemProps
  extends VariantProps<typeof chartContainerVariants> {
  // Accessibility
  ariaLabel?: string;
  aspectRatio?: "video" | "square" | "auto";

  // Styling
  className?: string;
  classNames?: {
    container?: string;
    header?: string;
    title?: string;
    description?: string;
    chart?: string;
  };
  config: ChartConfig;
  connectNulls?: boolean;
  // Chart configuration
  data: Record<string, unknown>[];

  // Chart options
  dataKeys?: string[];
  description?: string;
  dotSize?: number;
  filled?: boolean;

  // Chart dimensions
  height?: number | string;

  // Line-specific options
  lineType?: "linear" | "monotone" | "step" | "stepBefore" | "stepAfter";

  // Interaction
  onClick?: () => void;
  showDots?: boolean;
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  size?: "sm" | "md" | "lg";
  strokeWidth?: number;

  // Labels
  title?: string;

  // Styling variants
  variant?: "card" | "outline" | "ghost" | "minimal";
  xAxisKey?: string;
}

export function LineChartItem({
  data,
  config,
  dataKeys,
  xAxisKey = "name",
  showGrid = true,
  showXAxis = true,
  showYAxis = false,
  showLegend = false,
  showTooltip = true,
  lineType = "monotone",
  showDots = false,
  dotSize = 4,
  strokeWidth = 2,
  filled = false,
  connectNulls = false,
  title,
  description,
  variant = "card",
  size = "md",
  height,
  aspectRatio = "video",
  onClick,
  ariaLabel,
  className,
  classNames,
}: LineChartItemProps) {
  const chartDataKeys =
    dataKeys ?? Object.keys(config).filter((key) => config[key]?.label);

  const isClickable = Boolean(onClick);

  const containerClassName = cn(
    chartContainerVariants({ variant, size }),
    isClickable && "cursor-pointer",
    classNames?.container,
    className
  );

  const chartClassName = cn(
    aspectRatio === "video" && "aspect-video",
    aspectRatio === "square" && "aspect-square",
    aspectRatio === "auto" && "w-full",
    classNames?.chart
  );

  const chartAriaLabel = ariaLabel ?? (title ? `${title} chart` : "Line Chart");

  const renderChart = () => (
    <LineChart accessibilityLayer data={data}>
      {showGrid && <CartesianGrid vertical={false} />}
      {showXAxis && (
        <XAxis
          axisLine={false}
          dataKey={xAxisKey}
          tickLine={false}
          tickMargin={10}
        />
      )}
      {showYAxis && <YAxis axisLine={false} tickLine={false} tickMargin={10} />}
      {showTooltip && (
        <ChartTooltip content={<ChartTooltipContent />} cursor={false} />
      )}
      {showLegend && <ChartLegend content={<ChartLegendContent />} />}
      {chartDataKeys.map((key) => (
        <Line
          connectNulls={connectNulls}
          dataKey={key}
          dot={showDots ? { r: dotSize } : false}
          fill={filled ? `var(--color-${key})` : undefined}
          key={key}
          stroke={`var(--color-${key})`}
          strokeWidth={strokeWidth}
          type={lineType}
        />
      ))}
    </LineChart>
  );

  const content = (
    <>
      {(title ?? description) && (
        <div
          className={cn(
            "flex flex-col items-start justify-between gap-1",
            classNames?.header
          )}
        >
          {title && (
            <div
              className={cn(chartTitleVariants({ size }), classNames?.title)}
            >
              {title}
            </div>
          )}
          {description && (
            <div
              className={cn(
                chartDescriptionVariants({ size }),
                classNames?.description
              )}
            >
              {description}
            </div>
          )}
        </div>
      )}

      <ChartContainer
        aria-label={chartAriaLabel}
        className={chartClassName}
        config={config}
        style={height ? { height } : undefined}
      >
        {renderChart()}
      </ChartContainer>
    </>
  );

  if (isClickable) {
    return (
      <button className={containerClassName} onClick={onClick} type="button">
        {content}
      </button>
    );
  }

  return <div className={containerClassName}>{content}</div>;
}

export interface LineChartCollectionProps
  extends VariantProps<typeof chartContainerVariants> {
  className?: string;
  columns?: 1 | 2 | 3;
  gap?: "sm" | "md" | "lg";
  items: LineChartItemProps[];

  layout?: "stack" | "grid";

  onItemClick?: (item: LineChartItemProps, index: number) => void;
  size?: "sm" | "md" | "lg";

  variant?: "card" | "outline" | "ghost" | "minimal";
}

const chartCollectionVariants = cva("w-full", {
  variants: {
    layout: {
      stack: "flex flex-col",
      grid: "grid",
    },
    columns: {
      1: "grid-cols-1",
      2: "grid-cols-1 md:grid-cols-2",
      3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    },
    gap: {
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
    },
  },
  defaultVariants: {
    layout: "stack",
    gap: "md",
  },
});

export function LineChartCollection({
  items,
  layout = "stack",
  columns = 1,
  gap = "md",
  variant = "card",
  size = "md",
  onItemClick,
  className,
}: LineChartCollectionProps) {
  return (
    <div
      className={cn(
        chartCollectionVariants({
          layout,
          columns: layout === "grid" ? columns : undefined,
          gap,
        }),
        className
      )}
    >
      {items.map((item, index) => (
        <LineChartItem
          key={item.title ?? `chart-${index}`}
          onClick={
            onItemClick
              ? () => onItemClick(item as LineChartItemProps, index)
              : item.onClick
          }
          size={item.size ?? size}
          variant={item.variant ?? variant}
          {...item}
        />
      ))}
    </div>
  );
}
